import { format } from 'date-fns';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticsEvent, captureAnalyticsEvent } from '../../constant/Events';
import { useCompany } from '../../hooks/useCompany';
import { Info } from '../../assets/icons';
import {
  useAlignLicenseExpiryInfoMutation,
  useAlignLicenseExpiryMutation,
} from '../../services/license.api';
import { SCOPE_TYPE } from '../../services/utils';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Label,
  LabelType,
  Spinner,
  useGlobalModalContext,
} from '../_ui';
import { Form } from '../_ui/Form.component';

interface LicenseAlignProps {
  licenseIds: string[];
}

export const LicenseAlign = memo(({ licenseIds }: LicenseAlignProps) => {
  const { t } = useTranslation();
  const { userScope, companyId } = useCompany();
  const { hideModal } = useGlobalModalContext();
  const [triggerInfo, { data: alignInfo, isLoading: isInfoLoading }] =
    useAlignLicenseExpiryInfoMutation();
  const [triggerAlign, alignResult] = useAlignLicenseExpiryMutation();

  const requestParams = useMemo(
    () => ({
      scope: userScope ?? SCOPE_TYPE.COMPANY,
      companyId: companyId ?? '',
    }),
    [userScope, companyId],
  );

  useEffect(() => {
    triggerInfo({
      params: requestParams,
      body: { ids: licenseIds, dayOfMonth: 0 },
    });
  }, [triggerInfo, requestParams, licenseIds]);

  const handleAlign = () => {
    if (!alignInfo?.expiryDate) return;
    captureAnalyticsEvent(AnalyticsEvent.LICENSE_ALIGNMENT_EVENT);
    triggerAlign({
      params: requestParams,
      body: { ids: licenseIds, dayOfMonth: 0, expiryDate: alignInfo.expiryDate },
    });
  };

  return (
    <Form
      onSubmit={handleAlign}
      queryResult={alignResult}
      onQuerySuccess={() => hideModal()}
      onQueryFailed={() => hideModal()}
    >
      <div className='flex flex-col gap-4 pb-5'>
        {isInfoLoading ? (
          <Spinner />
        ) : (
          <>
            <Label
              text={t('license_align_description')}
              type={LabelType.LABEL_M}
              color={ColorType.BLACK}
            />
            <Label
              text={t('license_align_warning')}
              type={LabelType.LABEL_S_GREY4}
              color={ColorType.BLACK}
              icon={Info}
            />
            <hr />

            <div className='flex flex-row justify-between'>
              <Label
                text={t('license_align_number_of_licenses')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
              />
              <Label
                text={licenseIds?.length ? licenseIds.length : '—'}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
            </div>
            <div className='flex flex-row justify-between'>
              <Label
                text={t('license_align_number_of_ports')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
              />
              <Label
                text={alignInfo?.ports ?? '—'}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
            </div>

            <div className='flex flex-row justify-between'>
              <Label
                text={t('license_align_number_of_days')}
                type={LabelType.LABEL_S}
                color={ColorType.BLACK}
              />
              <Label
                text={alignInfo?.portDaysRemaining ?? '—'}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
            </div>
            <div className='flex flex-row justify-between'>
              <Label
                text={t('license_align_average_days')}
                type={LabelType.LABEL_S}
                color={ColorType.BLACK}
              />
              <Label
                text={alignInfo?.averageDaysRemaining ?? '—'}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
            </div>

            <div className='flex flex-row justify-between'>
              <Label
                text={t('license_align_expiry_date')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
              />
              <Label
                text={
                  alignInfo?.expiryDate
                    ? format(new Date(alignInfo.expiryDate), 'MMM d, yyyy')
                    : '—'
                }
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
            </div>
            {/*<div className='flex flex-row justify-between'>
              <Label
                text={t('license_align_rounded_days')}
                type={LabelType.LABEL_S}
                color={ColorType.BLACK}
              />
              <Label
                text={alignInfo?.extraTime ?? '—'}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
            </div>
            <div className='flex flex-row justify-between'>
              <Label
                text={t('license_align_rounded_unit_cost')}
                type={LabelType.LABEL_S}
                color={ColorType.BLACK}
              />
              <Label
                text={alignInfo?.extraLicenseUnits != null ? String(alignInfo.extraLicenseUnits) : '—'}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
            </div>*/}
          </>
        )}
        <div className='flex flex-col gap-2 mt-2'>
          <Button
            label={t('confirm')}
            size={ButtonSize.SMALL_FULL}
            type={ButtonType.PRIMARY}
            isSumbit
            dataTestId='licenseAlign'
            disabled={isInfoLoading || !alignInfo}
          />
        </div>
      </div>
    </Form>
  );
});

LicenseAlign.displayName = 'LicenseAlign';
